import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'bug',             label: 'Bug / something is broken' },
  { value: 'billing',         label: 'Billing / subscription' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'account',         label: 'Account / login' },
  { value: 'other',           label: 'Other' },
];

const APP_VERSION =
  (typeof window !== 'undefined' && window.__APP_VERSION__) ||
  process.env.REACT_APP_VERSION || 'web';

/** New support ticket form. */
export default function NewSupportTicket() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Pre-fill from the assistant handoff. Trim to the subject max-length so
  // a verbose assistant question doesn't bust the field.
  const prefillFromAssistant = (params.get('q') || '').slice(0, 200);
  const [subject, setSubject] = useState(prefillFromAssistant);
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState(
    prefillFromAssistant
      ? `Context from the virtual assistant:\n> ${prefillFromAssistant}\n\n`
      : ''
  );
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) return null;  // let AuthContext hydrate
  if (!isAuthenticated) { navigate('/login'); return null; }

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || description.trim().length < 10) {
      toast.error('Please add a short subject and a longer description.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const context = {
        // Auto-captured so Collin can jump straight to the right page.
        page_url: typeof document !== 'undefined' ? document.referrer || window.location.href : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        app_version: APP_VERSION,
      };
      const res = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subject, category, description, context }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Could not open ticket'));
        return;
      }
      toast.success('Ticket submitted — we usually reply within one business day.');
      navigate(`/support/${data.ticket.id}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title="Open a support ticket | Schedule Guru" description="Contact Schedule Guru support about a bug, billing question, or feature request." path="/support/new" />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/support">
          <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-support">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to tickets
          </Button>
        </Link>
        <h1 className="text-2xl font-black text-slate-900 mb-1">Open a support ticket</h1>
        <p className="text-slate-500 text-sm mb-5">
          Tell us what's going on. We'll reply by email and in-app.
        </p>

        <Card>
          <CardContent className="p-5">
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary — e.g. Live stats clock won't start"
                  maxLength={200}
                  required
                  data-testid="ticket-subject"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="category" data-testid="ticket-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value} data-testid={`ticket-category-${c.value}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened? Any steps to reproduce? Screenshots are helpful — reply with them after submission."
                  maxLength={5000}
                  rows={8}
                  required
                  data-testid="ticket-description"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  We automatically include your current page and browser info — no need to repeat them.
                </p>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#00247D] hover:bg-[#001A5C] text-white h-11"
                data-testid="submit-ticket-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send ticket</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
